/* eslint-disable camelcase */
import React, { useState, useMemo } from "react"
import Header from "../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  Button,
  Stack,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  Chip,
  LinearProgress,
  Autocomplete,
  TextField,
} from "@mui/material"
import fetchwrapper from "../../services/interceptors/fetchwrapper"
import BackIcon from "../../components/BackIcon"
import CustomBackdrop from "../../components/CustomBackdrop"
import CustomFieldsetAccordion from "../../components/CustomFieldsetAccordion"
import CustomAggregationSegregationComponent from "../../components/CustomAggregationSegregationComponent"
import CustomDatePicker from "../../components/CustomDatePicker"
import { useQuery } from "@tanstack/react-query"
import dayjs from "dayjs"
import { showWarning } from "../../api"

// Íconos Ejecutivos
import RefreshIcon from "@mui/icons-material/Refresh"
import SearchIcon from "@mui/icons-material/Search"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import StyleIcon from "@mui/icons-material/Style"
import MapIcon from "@mui/icons-material/Map"
import DownloadIcon from "@mui/icons-material/Download"
import AutoAwesomeMosaicIcon from "@mui/icons-material/AutoAwesomeMosaic"
import Inventory2Icon from "@mui/icons-material/Inventory2"

const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#2e7d32" },
    error: { main: "#d32f2f" },
    warning: { main: "#ed6c02" },
    info: { main: "#0288d1" },
  },
})

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(4),
  flexGrow: 1,
  padding: "0 16px",
  minHeight: "100vh",
}))

// Estilo para Tarjetas Ejecutivas (C-Level)
const KPICard = styled(Card)(({ theme, bgcolor, isSelected }) => ({
  borderRadius: "16px",
  boxShadow: isSelected ? "0 0 0 4px rgba(25, 108, 135, 0.5)" : "0 8px 24px rgba(0,0,0,0.08)",
  background: bgcolor || "#ffffff",
  color: bgcolor ? "#ffffff" : "#333",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  overflow: "hidden",
  transition: "all 0.2s ease-in-out",
  transform: isSelected ? "translateY(-4px) scale(1.02)" : "none",
  "&:hover": {
    transform: "translateY(-4px)",
  },
}))

// --- HOOKS DE REACT QUERY ---

function useGetCatalogosBodegas() {
  return useQuery({
    queryKey: ["CatalogosResumenBodegas"],
    queryFn: async () => {
      const response = await fetchwrapper(`/ResumenProductosbodegas/getCatalogosResumenBodegas`)
      const result = await response.json()
      return result.data?.bodegas || []
    },
    refetchOnWindowFocus: false,
  })
}

function useGetTablaInventario(filtros) {
  return useQuery({
    queryKey: ["TablaResumenBodegas", filtros],
    queryFn: async () => {
      const payload = {
        externalFilters: {
          bodegas: filtros.bodegas,
          fecVenDesde: filtros.fecVenDesde ? dayjs(filtros.fecVenDesde).format("YYYY-MM-DD") : "",
          fecVenHasta: filtros.fecVenHasta ? dayjs(filtros.fecVenHasta).format("YYYY-MM-DD") : "",
          soloVencidos: filtros.soloVencidos,
          soloPorVencer: filtros.soloPorVencer,
        },
      }
      const options = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
      const response = await fetchwrapper(`/ResumenProductosbodegas/getAllInfo`, options)
      const result = await response.json()
      return result.data || []
    },
    enabled: false,
  })
}

const ResumenProductosBodegas = () => {
  const [expanded, setExpanded] = useState(true)
  const [hasSearched, setHasSearched] = useState(false)

  // Estado que controla qué KPI se ha hecho clic (MERMA, RIESGO, VIGENTE, COMBINADO, CATALOGO, ZONA_...)
  // El valor por defecto al iniciar o buscar ahora es "COMBINADO"
  const [filtroActivoKPI, setFiltroActivoKPI] = useState("COMBINADO")

  const [filtros, setFiltros] = useState({
    bodegasCB: [],
    bodegas: [],
    fecVenDesde: null,
    fecVenHasta: null,
    soloVencidos: false,
    soloPorVencer: false,
  })

  // Queries
  const { data: listaBodegas = [], isLoading: loadBodegas } = useGetCatalogosBodegas()
  const {
    data: tablaData = [],
    isLoading: loadTabla,
    isFetching: fetchTabla,
    refetch: refetchTabla,
  } = useGetTablaInventario(filtros)

  const handleToggleFiltros = () => setExpanded(!expanded)

  const handleEjecutarBusqueda = () => {
    setFiltroActivoKPI("COMBINADO") // Resetear al nuevo KPI predeterminado al buscar
    if (filtros.bodegas.length === 0) {
      setFiltros((prev) => ({
        ...prev,
        bodegasCB: listaBodegas,
        bodegas: listaBodegas.map((v) => v.bodcodigo),
      }))

      setTimeout(async () => {
        setHasSearched(true)
        setExpanded(false)
        await refetchTabla()
      }, 50)
    } else {
      setHasSearched(true)
      setExpanded(false)
      refetchTabla()
    }
  }

  // --- CÁLCULO DINÁMICO DE KPIS EN EL CLIENTE ---
  const kpiCalculados = useMemo(() => {
    let bruto = 0
    let util = 0
    let vencidos = 0
    let riesgo = 0
    const skusUnicos = new Set()
    const zonasMap = {}

    tablaData.forEach((row) => {
      const currentStockBruto = Number(row.stock_bruto || 0)

      bruto += currentStockBruto
      util += Number(row.stock_util || 0)
      vencidos += Number(row.vencidos || 0)
      riesgo += Number(row.por_vencer || 0)

      if (row.artcodigo) skusUnicos.add(row.artcodigo)

      // Conteo de Posiciones Ocupadas por Zona (Solamente si hay stock_bruto > 0)
      if (row.zona && row.posicion && currentStockBruto > 0) {
        if (!zonasMap[row.zona]) zonasMap[row.zona] = new Set()
        // Combinamos la llave para contar posiciones únicas reales
        zonasMap[row.zona].add(`${row.pasillo}-${row.bahia}-${row.nivel}-${row.posicion}`)
      }
    })

    const posiciones_por_zona = Object.keys(zonasMap)
      .map((zona) => ({
        zona,
        posiciones_ocupadas: zonasMap[zona].size,
      }))
      .sort((a, b) => b.posiciones_ocupadas - a.posiciones_ocupadas)

    return {
      total_stock_bruto: bruto,
      total_stock_util: util,
      total_lotes_vencidos: vencidos,
      total_lotes_riesgo: riesgo,
      total_codigos_productos: skusUnicos.size,
      posiciones_por_zona,
    }
  }, [tablaData])

  // --- APLICACIÓN DEL FILTRO KPI HACIA LA TABLA ---
  const tablaDataFiltrada = useMemo(() => {
    // Ya no existe el caso "TODOS"
    if (filtroActivoKPI === "MERMA") return tablaData.filter((row) => Number(row.vencidos) > 0)
    if (filtroActivoKPI === "RIESGO") return tablaData.filter((row) => Number(row.por_vencer) > 0)
    if (filtroActivoKPI === "VIGENTE") return tablaData.filter((row) => Number(row.stock_util) > 0)

    // Filtro combinado (Muestra cualquier registro que tenga valor útil en merma, riesgo o vigente)
    if (filtroActivoKPI === "COMBINADO") {
      return tablaData.filter(
        (row) => Number(row.vencidos) > 0 || Number(row.por_vencer) > 0 || Number(row.stock_util) > 0,
      )
    }

    // Filtrado por Zona Dinámica (Solo mostrar registros de la zona que tengan stock_bruto > 0)
    if (filtroActivoKPI.startsWith("ZONA_")) {
      const targetZona = filtroActivoKPI.replace("ZONA_", "")
      return tablaData.filter((row) => row.zona === targetZona && Number(row.stock_bruto || 0) > 0)
    }

    if (filtroActivoKPI === "CATALOGO") {
      // Agrupa los productos para mostrar 1 sola línea por SKU omitiendo la ubicación
      const catalogoMap = {}
      tablaData.forEach((row) => {
        if (!catalogoMap[row.artcodigo]) {
          catalogoMap[row.artcodigo] = {
            ...row,
            zona: "MÚLTIPLES",
            pasillo: "-",
            posicion: "-",
            stock_bruto: Number(row.stock_bruto || 0),
            stock_util: Number(row.stock_util || 0),
            vencidos: Number(row.vencidos || 0),
            por_vencer: Number(row.por_vencer || 0),
          }
        } else {
          catalogoMap[row.artcodigo].stock_bruto += Number(row.stock_bruto || 0)
          catalogoMap[row.artcodigo].stock_util += Number(row.stock_util || 0)
          catalogoMap[row.artcodigo].vencidos += Number(row.vencidos || 0)
          catalogoMap[row.artcodigo].por_vencer += Number(row.por_vencer || 0)
        }
      })
      return Object.values(catalogoMap)
    }

    return tablaData
  }, [tablaData, filtroActivoKPI])

  // Función para exportar la data filtrada
  const handleExportarCSV = () => {
    if (!tablaDataFiltrada || tablaDataFiltrada.length === 0) {
      return showWarning("No hay datos disponibles para exportar.")
    }

    const headers = [
      "Bodega",
      "Familia",
      "Marca",
      "Código",
      "Descripción",
      "Zona",
      "Pasillo",
      "Posición",
      "Stock Físico",
      "Stock Útil",
      "Lotes Vencidos",
      "Por Vencer (Riesgo)",
      "Fec. Próx. Caducidad",
    ]

    const csvContent = [
      headers.join(","),
      ...tablaDataFiltrada.map((row) => {
        return [
          `"${row.boddescri || ""}"`,
          `"${row.familia || ""}"`,
          `"${row.marca || ""}"`,
          `"${row.artcodigo || ""}"`,
          `"${row.artdescri ? row.artdescri.replace(/"/g, '""') : ""}"`,
          `"${row.zona || ""}"`,
          `"${row.pasillo || ""}"`,
          `"${row.posicion || ""}"`,
          row.stock_bruto || 0,
          row.stock_util || 0,
          row.vencidos || 0,
          row.por_vencer || 0,
          row.fecha_vencimiento ? dayjs(row.fecha_vencimiento).format("DD/MM/YYYY") : "N/A",
        ].join(",")
      }),
    ].join("\n")

    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.setAttribute("download", `Resumen_Inventario_${filtroActivoKPI}_${dayjs().format("YYYYMMDD_HHmm")}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const isLoadingGlobal = loadBodegas || loadTabla || fetchTabla

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <CustomBackdrop isLoading={isLoadingGlobal} />

        <Box className={StyledRoot}>
          {/* =================================================================== */}
          {/* SECCIÓN A: CRITERIOS DE BÚSQUEDA Y FILTROS                          */}
          {/* =================================================================== */}
          <Box mb={4}>
            <CustomFieldsetAccordion title="Criterios de Búsqueda" expanded={expanded} onToggle={handleToggleFiltros}>
              <Grid container spacing={2} alignItems="center" pt={1}>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    multiple
                    options={listaBodegas}
                    getOptionLabel={(option) => `${option.bodcodigo} - ${option.boddescri}`}
                    value={filtros.bodegasCB}
                    isOptionEqualToValue={(option, value) => option.bodcodigo === value.bodcodigo}
                    onChange={(e, newValue) => {
                      setFiltros((prev) => ({
                        ...prev,
                        bodegasCB: newValue,
                        bodegas: newValue.map((v) => v.bodcodigo),
                      }))
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Seleccione Bodega(s) para Analizar *"
                        placeholder="Buscar bodegas..."
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <CustomDatePicker
                    label="Vencimiento Desde"
                    value={filtros.fecVenDesde}
                    setValue={(v) => setFiltros((prev) => ({ ...prev, fecVenDesde: v }))}
                    isOptional={true}
                    format="DD/MM/YYYY"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomDatePicker
                    label="Vencimiento Hasta"
                    value={filtros.fecVenHasta}
                    setValue={(v) => setFiltros((prev) => ({ ...prev, fecVenHasta: v }))}
                    isOptional={true}
                    format="DD/MM/YYYY"
                  />
                </Grid>

                <Grid item xs={12} md={8}>
                  <Stack direction="row" spacing={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          color="error"
                          checked={filtros.soloVencidos}
                          onChange={(e) => setFiltros((prev) => ({ ...prev, soloVencidos: e.target.checked }))}
                        />
                      }
                      label={<b>Filtrar solo Lotes Vencidos (Merma)</b>}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          color="warning"
                          checked={filtros.soloPorVencer}
                          onChange={(e) => setFiltros((prev) => ({ ...prev, soloPorVencer: e.target.checked }))}
                        />
                      }
                      label={<b>Filtrar solo Riesgo (Por Vencer)</b>}
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12} md={4} display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<SearchIcon />}
                    onClick={handleEjecutarBusqueda}
                    sx={{ px: 4, fontWeight: "bold", borderRadius: "8px" }}
                  >
                    Ejecutar Búsqueda
                  </Button>
                </Grid>
              </Grid>
            </CustomFieldsetAccordion>
          </Box>

          {/* =================================================================== */}
          {/* SECCIÓN B: KPIS EJECUTIVOS (INTERACTIVOS)                           */}
          {/* =================================================================== */}
          <Box mb={4}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5" color="primary" fontWeight="bold" sx={{ display: "flex", alignItems: "center" }}>
                <Inventory2Icon sx={{ mr: 1, fontSize: 32 }} />
                Dashboard Ejecutivo (Haga clic en un indicador para filtrar la tabla)
              </Typography>
              <Tooltip title="Actualizar Tabla e Indicadores">
                <IconButton
                  onClick={() => refetchTabla()}
                  sx={{ bgcolor: "white", boxShadow: 1 }}
                  disabled={!hasSearched}
                >
                  <RefreshIcon color={hasSearched ? "primary" : "disabled"} />
                </IconButton>
              </Tooltip>
            </Stack>

            <Grid container spacing={2}>
              {/* 1. MERMA (Rojo) */}
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  bgcolor="linear-gradient(135deg, #d32f2f 0%, #9a0007 100%)"
                  isSelected={filtroActivoKPI === "MERMA"}
                >
                  <CardActionArea
                    onClick={() => setFiltroActivoKPI(filtroActivoKPI === "MERMA" ? "COMBINADO" : "MERMA")}
                    sx={{ height: "100%", color: "inherit" }}
                  >
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ opacity: 0.8, textTransform: "uppercase", color: "white" }}>
                        Merma: Vencidos
                      </Typography>
                      <Typography variant="h3" fontWeight="bold" mt={1} color="white">
                        {Number(kpiCalculados.total_lotes_vencidos).toLocaleString()}
                      </Typography>
                      <ErrorOutlineIcon
                        sx={{
                          position: "absolute",
                          right: -10,
                          bottom: -10,
                          fontSize: 100,
                          opacity: 0.15,
                          color: "white",
                        }}
                      />
                    </CardContent>
                  </CardActionArea>
                </KPICard>
              </Grid>

              {/* 2. RIESGO (Naranja) */}
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  bgcolor="linear-gradient(135deg, #ed6c02 0%, #e65100 100%)"
                  isSelected={filtroActivoKPI === "RIESGO"}
                >
                  <CardActionArea
                    onClick={() => setFiltroActivoKPI(filtroActivoKPI === "RIESGO" ? "COMBINADO" : "RIESGO")}
                    sx={{ height: "100%", color: "inherit" }}
                  >
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ opacity: 0.8, textTransform: "uppercase", color: "white" }}>
                        Riesgo: Por Vencer
                      </Typography>
                      <Typography variant="h3" fontWeight="bold" mt={1} color="white">
                        {Number(kpiCalculados.total_lotes_riesgo).toLocaleString()}
                      </Typography>
                      <WarningAmberIcon
                        sx={{
                          position: "absolute",
                          right: -10,
                          bottom: -10,
                          fontSize: 100,
                          opacity: 0.15,
                          color: "white",
                        }}
                      />
                    </CardContent>
                  </CardActionArea>
                </KPICard>
              </Grid>

              {/* 3. STOCK VIGENTE (Verde) */}
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  bgcolor="linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)"
                  isSelected={filtroActivoKPI === "VIGENTE"}
                >
                  <CardActionArea
                    onClick={() => setFiltroActivoKPI(filtroActivoKPI === "VIGENTE" ? "COMBINADO" : "VIGENTE")}
                    sx={{ height: "100%", color: "inherit" }}
                  >
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ opacity: 0.8, textTransform: "uppercase", color: "white" }}>
                        Stock Vigente
                      </Typography>
                      <Typography variant="h3" fontWeight="bold" mt={1} color="white">
                        {Number(kpiCalculados.total_stock_util).toLocaleString()}
                      </Typography>
                      <CheckCircleOutlineIcon
                        sx={{
                          position: "absolute",
                          right: -10,
                          bottom: -10,
                          fontSize: 100,
                          opacity: 0.15,
                          color: "white",
                        }}
                      />
                    </CardContent>
                  </CardActionArea>
                </KPICard>
              </Grid>

              {/* 4. MERMA + RIESGO + VIGENTE (Morado - Nuevo KPI Predeterminado) */}
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  bgcolor="linear-gradient(135deg, #7b1fa2 0%, #4a148c 100%)"
                  isSelected={filtroActivoKPI === "COMBINADO"}
                >
                  <CardActionArea
                    onClick={() => setFiltroActivoKPI(filtroActivoKPI === "COMBINADO" ? "COMBINADO" : "COMBINADO")}
                    sx={{ height: "100%", color: "inherit" }}
                  >
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ opacity: 0.8, textTransform: "uppercase", color: "white" }}>
                        Merma + Riesgo + Vigente
                      </Typography>
                      <Typography variant="h3" fontWeight="bold" mt={1} color="white">
                        {Number(
                          kpiCalculados.total_lotes_vencidos +
                            kpiCalculados.total_lotes_riesgo +
                            kpiCalculados.total_stock_util,
                        ).toLocaleString()}
                      </Typography>
                      <AutoAwesomeMosaicIcon
                        sx={{
                          position: "absolute",
                          right: -10,
                          bottom: -10,
                          fontSize: 100,
                          opacity: 0.15,
                          color: "white",
                        }}
                      />
                    </CardContent>
                  </CardActionArea>
                </KPICard>
              </Grid>

              {/* Fila Secundaria de KPIs: SKUs y Zonas */}
              <Grid item xs={12} md={4}>
                <KPICard isSelected={filtroActivoKPI === "CATALOGO"}>
                  <CardActionArea
                    onClick={() => setFiltroActivoKPI(filtroActivoKPI === "CATALOGO" ? "COMBINADO" : "CATALOGO")}
                    sx={{ height: "100%" }}
                  >
                    <CardContent
                      sx={{ display: "flex", flexDirection: "column", height: "100%", justifyContent: "center" }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{ p: 2, bgcolor: "#f3e5f5", borderRadius: "12px" }}>
                          <StyleIcon sx={{ color: "#9c27b0", fontSize: 40 }} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary" textTransform="uppercase">
                            Catálogo de Productos
                          </Typography>
                          <Typography variant="h4" fontWeight="bold" color="#9c27b0">
                            {Number(kpiCalculados.total_codigos_productos).toLocaleString()} SKUs
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </KPICard>
              </Grid>

              <Grid item xs={12} md={8}>
                <KPICard>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                      <MapIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        Posiciones Ocupadas por Zona de Almacenamiento
                      </Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      {(kpiCalculados.posiciones_por_zona || []).map((z, idx) => {
                        const esZonaSeleccionada = filtroActivoKPI === `ZONA_${z.zona}`
                        return (
                          <Grid item xs={12} sm={6} md={4} key={idx}>
                            <Box
                              onClick={() => setFiltroActivoKPI(esZonaSeleccionada ? "COMBINADO" : `ZONA_${z.zona}`)}
                              sx={{
                                cursor: "pointer",
                                p: 1,
                                borderRadius: 2,
                                bgcolor: esZonaSeleccionada ? "rgba(25, 108, 135, 0.08)" : "transparent",
                                border: esZonaSeleccionada ? "1px solid #196C87" : "1px solid transparent",
                                transition: "all 0.2s",
                                "&:hover": {
                                  bgcolor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <Box display="flex" justifyContent="space-between" mb={0.5}>
                                <Typography
                                  variant="body2"
                                  fontWeight={esZonaSeleccionada ? "bold" : "medium"}
                                  color={esZonaSeleccionada ? "primary" : "textPrimary"}
                                >
                                  {z.zona}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color={esZonaSeleccionada ? "primary" : "textSecondary"}
                                  fontWeight={esZonaSeleccionada ? "bold" : "regular"}
                                >
                                  {z.posiciones_ocupadas} pos.
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(100, (z.posiciones_ocupadas / 500) * 100)}
                                sx={{ height: 8, borderRadius: 4, bgcolor: "#e0e0e0" }}
                                color={idx % 2 === 0 ? "primary" : "info"}
                              />
                            </Box>
                          </Grid>
                        )
                      })}
                      {(!kpiCalculados.posiciones_por_zona || kpiCalculados.posiciones_por_zona.length === 0) && (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ width: "100%", textAlign: "center", py: 2 }}
                        >
                          No hay información de zonas disponible.
                        </Typography>
                      )}
                    </Grid>
                  </CardContent>
                </KPICard>
              </Grid>
            </Grid>
          </Box>

          {/* =================================================================== */}
          {/* SECCIÓN C: TABLA DINÁMICA DE INTELIGENCIA                           */}
          {/* =================================================================== */}
          <Box mt={3} flexGrow={1}>
            {hasSearched && tablaDataFiltrada.length > 0 && (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">
                    Mostrando resultados para:{" "}
                    <Chip label={filtroActivoKPI.replace("ZONA_", "ZONA: ")} color="primary" size="small" />
                  </Typography>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportarCSV}
                    sx={{ fontWeight: "bold", bgcolor: "white" }}
                  >
                    Exportar a Excel / CSV
                  </Button>
                </Stack>

                <CustomAggregationSegregationComponent
                  data={tablaDataFiltrada}
                  columnsConfig={({ totalSumStockBruto, totalSumStockUtil, totalSumVencidos, totalSumPorVencer }) => [
                    { accessorKey: "boddescri", header: "Bodega", size: 150 },
                    { accessorKey: "familia", header: "Familia", size: 150 },
                    { accessorKey: "marca", header: "Marca", size: 150 },
                    {
                      accessorKey: "artcodigo",
                      header: "Código",
                      size: 120,
                      Cell: ({ cell }) => <b>{cell.getValue()}</b>,
                    },
                    { accessorKey: "artdescri", header: "Descripción del Producto", size: 300 },
                    { accessorKey: "zona", header: "Zona", size: 120 },
                    { accessorKey: "pasillo", header: "Pasillo", size: 100 },
                    { accessorKey: "posicion", header: "Posición", size: 100 },
                    {
                      accessorKey: "stock_bruto",
                      header: "Stock Físico",
                      size: 150,
                      accessorFn: (row) => Number(row.stock_bruto) || 0,
                      aggregationFn: "sum",
                      AggregatedCell: ({ cell }) => (
                        <Box color="primary.main" fontWeight="bold">
                          {cell.getValue().toLocaleString()}
                        </Box>
                      ),
                      Cell: ({ cell }) => (
                        <span style={{ fontWeight: "bold", color: "#196C87" }}>{cell.getValue().toLocaleString()}</span>
                      ),
                      Footer: () => (
                        <Stack>
                          Total Físico:
                          <Box color="primary.main" fontWeight="bold">
                            {Number(totalSumStockBruto).toLocaleString()}
                          </Box>
                        </Stack>
                      ),
                    },
                    {
                      accessorKey: "stock_util",
                      header: "Stock Útil",
                      size: 150,
                      accessorFn: (row) => Number(row.stock_util) || 0,
                      aggregationFn: "sum",
                      AggregatedCell: ({ cell }) => (
                        <Box color="success.main" fontWeight="bold">
                          {cell.getValue().toLocaleString()}
                        </Box>
                      ),
                      Cell: ({ cell }) => (
                        <span style={{ fontWeight: "bold", color: "#2e7d32" }}>{cell.getValue().toLocaleString()}</span>
                      ),
                      Footer: () => (
                        <Stack>
                          Total Útil:
                          <Box color="success.main" fontWeight="bold">
                            {Number(totalSumStockUtil).toLocaleString()}
                          </Box>
                        </Stack>
                      ),
                    },
                    {
                      accessorKey: "vencidos",
                      header: "Lotes Vencidos",
                      size: 150,
                      accessorFn: (row) => Number(row.vencidos) || 0,
                      aggregationFn: "sum",
                      AggregatedCell: ({ cell }) => (
                        <Box color="error.main" fontWeight="bold">
                          {cell.getValue().toLocaleString()}
                        </Box>
                      ),
                      Cell: ({ cell }) => {
                        const val = cell.getValue()
                        return val > 0 ? (
                          <Chip label={val.toLocaleString()} color="error" size="small" sx={{ fontWeight: "bold" }} />
                        ) : (
                          <span>0</span>
                        )
                      },
                      Footer: () => (
                        <Stack>
                          Total Vencidos:
                          <Box color="error.main" fontWeight="bold">
                            {Number(totalSumVencidos).toLocaleString()}
                          </Box>
                        </Stack>
                      ),
                    },
                    {
                      accessorKey: "por_vencer",
                      header: "Por Vencer (Riesgo)",
                      size: 160,
                      accessorFn: (row) => Number(row.por_vencer) || 0,
                      aggregationFn: "sum",
                      AggregatedCell: ({ cell }) => (
                        <Box color="warning.main" fontWeight="bold">
                          {cell.getValue().toLocaleString()}
                        </Box>
                      ),
                      Cell: ({ cell }) => {
                        const val = cell.getValue()
                        return val > 0 ? (
                          <Chip
                            label={val.toLocaleString()}
                            color="warning"
                            size="small"
                            sx={{ fontWeight: "bold", color: "black" }}
                          />
                        ) : (
                          <span>0</span>
                        )
                      },
                      Footer: () => (
                        <Stack>
                          Total Riesgo:
                          <Box color="warning.main" fontWeight="bold">
                            {Number(totalSumPorVencer).toLocaleString()}
                          </Box>
                        </Stack>
                      ),
                    },
                    {
                      accessorKey: "fecha_vencimiento",
                      header: "Fec. Próx. Caducidad",
                      size: 150,
                      Cell: ({ cell }) => {
                        const val = cell.getValue()
                        return <span>{val ? dayjs(val).format("DD/MM/YYYY") : "N/A"}</span>
                      },
                    },
                  ]}
                  aggregations={{
                    totalSumStockBruto: (data) => data.reduce((acc, curr) => acc + Number(curr.stock_bruto || 0), 0),
                    totalSumStockUtil: (data) => data.reduce((acc, curr) => acc + Number(curr.stock_util || 0), 0),
                    totalSumVencidos: (data) => data.reduce((acc, curr) => acc + Number(curr.vencidos || 0), 0),
                    totalSumPorVencer: (data) => data.reduce((acc, curr) => acc + Number(curr.por_vencer || 0), 0),
                  }}
                  tableOptions={{
                    initialState: { sorting: [{ id: "artdescri", desc: false }] },
                  }}
                />
              </>
            )}

            {/* EMPTY STATE ELEGANTE PARA EJECUTIVOS */}
            {(!hasSearched || (hasSearched && tablaDataFiltrada.length === 0 && !loadTabla && !fetchTabla)) && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "300px",
                  border: "2px dashed #cfd8dc",
                  borderRadius: "16px",
                  backgroundColor: "#ffffff",
                  p: 4,
                  mt: 2,
                }}
              >
                <SearchIcon sx={{ fontSize: 60, color: "#b0bec5", mb: 2 }} />
                <Typography variant="h6" color="textSecondary" fontWeight="bold">
                  {hasSearched
                    ? `No se encontraron resultados para la vista: ${filtroActivoKPI.replace("ZONA_", "ZONA: ")}.`
                    : "El panel analítico se encuentra a la espera."}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mt: 1, maxWidth: "500px", textAlign: "center" }}
                >
                  {hasSearched
                    ? "Intente seleccionar 'Merma + Riesgo + Vigente' en los indicadores superiores o modifique los criterios de búsqueda."
                    : "Seleccione una o más bodegas en los 'Criterios de Búsqueda' y presione 'Ejecutar Búsqueda' para generar los indicadores y la tabla dinámica."}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default ResumenProductosBodegas
